import { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { useMatch, useNavigate } from 'react-router';

import useGetUuid from 'components/accountSettings/utils';
import { DataTableCellProps, TableCell } from 'components/dataTable';
import { CopyMenuItem } from 'components/dataTable/cells';
import { ActionMenu, MenuItem } from 'components/dataTable/cells/actionMenu';

import RoutePath from 'router/path';

import { CustodialWalletTransaction, CustodialWalletTransactionType } from 'types/custodialWallet';

import { KEYS, LABELS } from '../../keys';
import { isRefundMovement } from '../../utils';

export const ActionCell = ({ row }: DataTableCellProps) => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const getTransactionId = (row: CustodialWalletTransaction) => {
    if (isRefundMovement(row.type)) {
      return row.refundUuid;
    }

    if (row.type === CustodialWalletTransactionType.MerchantTopup) {
      return row.topupUuid;
    }

    return row.chargeUuid;
  };

  const getActions = (row: CustodialWalletTransaction) => {
    if (isRefundMovement(row.type)) {
      return (
        <>
          <CopyMenuItem value={transactionUuid ?? ''}>{t(LABELS.TABLE_ACTIONS.COPY_ORDER_ID)}</CopyMenuItem>
          <MenuItem onClick={navigateToOrderDetails}>{t(LABELS.TABLE_ACTIONS.OPEN_ORDER_DETAILS)}</MenuItem>
        </>
      );
    }

    const mapActionsToType: Partial<Record<CustodialWalletTransactionType, ReactNode>> = {
      [CustodialWalletTransactionType.MerchantTopup]: (
        <MenuItem onClick={navigateToTopupDetails}>{t(LABELS.TABLE_ACTIONS.OPEN_TOPUP_DETAILS)}</MenuItem>
      ),
    };
    return mapActionsToType[row.type];
  };

  const match = useMatch(KEYS.ROUTE_MATCH);
  const merchantUuid = useGetUuid() || match?.params?.uuid || '';
  const transactionUuid = getTransactionId(row as unknown as CustodialWalletTransaction);

  const navigateToTopupDetails = () => {
    if (!transactionUuid) {
      return;
    }

    navigate(RoutePath.pwc.refundAccountTransactionById(merchantUuid, transactionUuid));
  };

  const navigateToOrderDetails = () => {
    if (!row.orderUuid) {
      return;
    }

    navigate(RoutePath.pwc.orderById(row.orderUuid));
  };

  return (
    <TableCell>
      <ActionMenu title={t(LABELS.TABLE_ACTIONS.TITLE)}>{getActions(row as unknown as CustodialWalletTransaction)}</ActionMenu>
    </TableCell>
  );
};
